// src/pages/redress/OffersScreen.tsx
//
// Offers screen for the FCA Motor Finance Redress Scheme (Phase 2, M6-4).
// Implements Screens.docx "Offer" and Work flows.pdf pages 2-3:
//
//   * Early-max-settlement / decision (PRD|RD) / remedy-type controls.
//   * Offer breakdown table (Offer made / Arrears / Complex Law deduction /
//     VAT / Total due to client) with Compute + Save actions.
//   * VAT shown inline on the CL deduction row (£x + £y inc VAT).
//   * Arrears eligibility flags (sameAgreementOnly / notUnderDispute).
//   * Dates panel -- key workflow dates, each editable with a Save.
//   * PRD response / RD response read-only status panel.
//   * Action strip -- buttons to drive the state machine: Mark PRD sent,
//     Record client accept / reject, Acknowledge objection, Mark FD
//     received, Mark paid, Escalate to RD, Mark RD sent. Each is disabled
//     when the current status is incompatible. Disabled buttons carry a
//     tooltip explaining the required status.
//   * Letter downloads panel -- PRD, FD and FOS referral docx downloads.
//   * "Next action" banner at the top that nudges ops to the earliest
//     upcoming deadline.
//
// All reads drive off agreement.offer / agreement.dates /
// agreement.workflow.status (agreement-scoped shape, Phase 2).

import React, { useCallback, useMemo, useState } from 'react';

import {
  Agreement,
  AgreementDates,
  Offer,
  OfferBreakdown,
  OfferDecision,
  PrdResponse,
  PrdResponseRecord,
  RdResponse,
  RdResponseRecord,
  RedressCase,
  RedressStatus,
  RemedyType,
  canTransition,
  offerArrearsAmount,
  offerArrearsFlags,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  bannerInfoStyle,
  bannerSuccessStyle,
  buttonDanger,
  buttonPrimary,
  buttonSecondary,
  colors,
  fieldsetStyle,
  humaniseStatus,
  inputStyle,
  labelStackStyle,
  legendStyle,
  pillDanger,
  pillNeutral,
  pillSuccess,
  pillWarning,
  tableStyle,
  tdStyle,
} from './styles';

interface OffersScreenProps {
  claimId: string;
  redressCase: RedressCase;
  agreement: Agreement;
  agreementId: string;
  onCaseUpdated: (c: RedressCase) => void;
}

const DATE_FIELDS: Array<{ key: keyof AgreementDates; label: string }> = [
  // P1-8 (H7): the standardised PRD anchor key. The legacy 'prdDate' is
  // NEVER promoted into this editable draft (Batch D) -- a user Save must
  // not silently persist a legacy-derived anchor; the field renders the
  // stored provisionalRedressDate only, empty when absent.
  { key: 'provisionalRedressDate', label: 'PRD Date' },
  { key: 'finalRedressDate', label: 'Final Redress Date' },
  { key: 'moneyDueDate', label: 'Money Due Date' },
  { key: 'paymentSentToClientDate', label: 'Payment Sent Date' },
  { key: 'paymentReceivedDate', label: 'Payment Received Date' },
  { key: 'objectionDueDate', label: 'Objection Due Date' },
  { key: 'acknowledgementDate', label: 'Objection Acknowledged Date' },
  { key: 'formalObjectionDueDate', label: 'Objection Evidence Due Date' },
  { key: 'responseDate', label: 'Revised Redress Date' },
  { key: 'fosDeadline', label: 'FOS Deadline' },
];

function parseMoney(raw: string): number {
  if (raw === '' || raw === '-') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

// L4: letter downloads use ``responseType: 'blob'``, so an error
// response's JSON body arrives as a Blob — ``extractError`` found nothing
// on it and the UI showed only the generic fallback. Read the Blob text
// and surface the backend's ``error``/``reason`` when it parses as JSON.
async function extractDownloadError(
  err: unknown,
  fallback: string,
): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text) as { error?: string; reason?: string };
        return parsed.error || parsed.reason || fallback;
      } catch {
        return text.trim() || fallback;
      }
    } catch {
      return fallback;
    }
  }
  return extractError(err, fallback);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// P0-4: the backend stores workflow.prdResponse / rdResponse as objects
// (`{}` skeleton before any response; `{value, date, notes}` once
// recorded); legacy records may hold the bare string. Extract the value
// defensively so a shape surprise can never crash the screen again.
function responseValue(
  r: PrdResponseRecord | RdResponseRecord | string | null | undefined,
): string {
  return typeof r === 'string' ? r : r?.value || 'no_response';
}

// Map PRD/RD response values to a pill style.
function pillForResponse(
  response: PrdResponseRecord | RdResponseRecord | string | null | undefined,
): React.CSSProperties {
  const value = responseValue(response);
  if (value === 'accept') return pillSuccess;
  if (value === 'object' || value === 'object_fos') return pillWarning;
  if (value === 'opt_out') return pillDanger;
  return pillNeutral;
}

// Map agreement status to the earliest upcoming deadline label the user
// cares about. Takes the agreement (Phase 2 — status lives on workflow).
function deriveNextAction(agreement: Agreement): string {
  const d = agreement.dates || {};
  const status = agreement.workflow.status;
  if (status === 'prd_received') return 'Send PRD to client';
  if (status === 'prd_sent_to_client') {
    const hint = d.objectionDueDate
      ? `Awaiting PRD response by ${d.objectionDueDate}`
      : 'Awaiting PRD response (1mo) → escalates to RD';
    return hint;
  }
  if (status === 'awaiting_rd') {
    // Batch G (CONRED 5.4.37R(6)): on PRD lapse the LENDER must issue an
    // RD confirming the PRD — rdExpectedByDate is the tracked deadline.
    return d.rdExpectedByDate
      ? `Awaiting lender RD — expected by ${d.rdExpectedByDate}`
      : 'Issue RD to client';
  }
  if (status === 'rd_sent_to_client') return 'Awaiting RD response (6mo)';
  if (status === 'client_accepted') return 'Awaiting FD from lender';
  if (status === 'client_objecting') return 'Acknowledge objection';
  if (status === 'objection_submitted' && d.responseDate) {
    return `Awaiting revised redress by ${d.responseDate}`;
  }
  if (status === 'fd_received' && d.moneyDueDate) {
    return `Payment due by ${d.moneyDueDate}`;
  }
  if (status === 'rejected_fos_referral' && d.fosDeadline) {
    return `FOS deadline ${d.fosDeadline}`;
  }
  if (status === 'paid') return 'Case paid';
  if (status === 'opted_out') return 'Opted out (charge raised if offer existed)';
  if (status === 'rd_no_response_closed') return 'RD lapsed — closed & charged';
  return `Status: ${humaniseStatus(status)}`;
}

export default function OffersScreen({
  claimId,
  redressCase,
  agreement,
  agreementId,
  onCaseUpdated,
}: OffersScreenProps): React.ReactElement {
  // --- Offer field state -----------------------------------------------------

  // Early max settlement / decision / remedy type — new Phase 2 controls.
  const [earlyMax, setEarlyMax] = useState<boolean>(
    Boolean(agreement.offer?.earlyMaxSettlement),
  );
  const [decision, setDecision] = useState<OfferDecision>(
    agreement.offer?.decision ?? 'PRD',
  );
  const [remedyType, setRemedyType] = useState<RemedyType>(
    agreement.offer?.remedyType ?? 'hybrid',
  );

  // Arrears — flat number on the wire (H13); offerArrearsAmount/-Flags
  // also read the legacy object shape from pre-fix documents.
  const initialArrears = offerArrearsAmount(agreement.offer);
  const initialFlags = offerArrearsFlags(agreement.offer);
  const [arrearsAmount, setArrearsAmount] = useState<string>(
    initialArrears !== undefined ? String(initialArrears) : '',
  );
  const [sameAgreementOnly, setSameAgreementOnly] = useState<boolean>(
    initialFlags.sameAgreementOnly,
  );
  const [notUnderDispute, setNotUnderDispute] = useState<boolean>(
    initialFlags.notUnderDispute,
  );

  // Offer amount (numeric string while editing).
  const [offerAmount, setOfferAmount] = useState<string>(
    agreement.offer?.offerAmount !== undefined
      ? String(agreement.offer.offerAmount)
      : '',
  );

  const [breakdown, setBreakdown] = useState<OfferBreakdown | null>(
    agreement.offer && agreement.offer.totalDueToClient !== undefined
      ? {
          offerAmount: agreement.offer.offerAmount ?? 0,
          arrears: offerArrearsAmount(agreement.offer) ?? 0,
          complexLawDeduction: agreement.offer.complexLawDeduction ?? 0,
          vat: agreement.offer.vat ?? 0,
          totalDueToClient: agreement.offer.totalDueToClient ?? 0,
        }
      : null,
  );

  // L4: the inputs the current breakdown was computed FROM. Save is
  // blocked when offerAmount/arrears drift after the last Compute, so a
  // stale CL deduction / VAT / total can never be persisted.
  const [computedFor, setComputedFor] = useState<{
    offerAmount: number;
    arrears: number;
  } | null>(
    agreement.offer && agreement.offer.totalDueToClient !== undefined
      ? {
          offerAmount: agreement.offer.offerAmount ?? 0,
          arrears: offerArrearsAmount(agreement.offer) ?? 0,
        }
      : null,
  );

  const [dateDraft, setDateDraft] = useState<AgreementDates>(
    agreement.dates ?? {},
  );

  // L4: the client accept/reject actions carry their OWN response date
  // (previously they silently reused the "Objection Acknowledged Date"
  // draft). Empty -> the backend stamps today.
  const [responseDate, setResponseDate] = useState<string>('');

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // --- Local helpers ----------------------------------------------------------

  const resetNotices = useCallback((): void => {
    setError(null);
    setMessage(null);
  }, []);

  const onCaseChanged = useCallback(
    (next: RedressCase): void => {
      onCaseUpdated(next);
      // Refresh date draft from the updated agreement.
      const nextAgreement = next.agreements.find(
        (a) => a.agreementId === agreementId,
      );
      if (nextAgreement) {
        setDateDraft(nextAgreement.dates ?? {});
        if (nextAgreement.offer?.totalDueToClient !== undefined) {
          setBreakdown({
            offerAmount: nextAgreement.offer.offerAmount ?? 0,
            arrears: offerArrearsAmount(nextAgreement.offer) ?? 0,
            complexLawDeduction: nextAgreement.offer.complexLawDeduction ?? 0,
            vat: nextAgreement.offer.vat ?? 0,
            totalDueToClient: nextAgreement.offer.totalDueToClient ?? 0,
          });
          setComputedFor({
            offerAmount: nextAgreement.offer.offerAmount ?? 0,
            arrears: offerArrearsAmount(nextAgreement.offer) ?? 0,
          });
        }
      }
    },
    [onCaseUpdated, agreementId],
  );

  const runAction = useCallback(
    async (
      label: string,
      fn: () => Promise<RedressCase>,
      successMessage: string,
    ): Promise<void> => {
      setBusy(label);
      resetNotices();
      try {
        const updated = await fn();
        onCaseChanged(updated);
        setMessage(successMessage);
      } catch (err: unknown) {
        setError(extractError(err, `${label} failed`));
      } finally {
        setBusy(null);
      }
    },
    [onCaseChanged, resetNotices],
  );

  // --- Button handlers -------------------------------------------------------

  const handleCompute = async (): Promise<void> => {
    setBusy('compute');
    resetNotices();
    try {
      const b = await redressApi.computeOfferBreakdown(
        claimId,
        redressCase.id,
        agreementId,
        parseMoney(offerAmount),
        parseMoney(arrearsAmount),
      );
      setBreakdown(b);
      setComputedFor({
        offerAmount: parseMoney(offerAmount),
        arrears: parseMoney(arrearsAmount),
      });
      setMessage('Breakdown computed (not yet saved).');
    } catch (err: unknown) {
      setError(extractError(err, 'Compute failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleSaveOffer = async (): Promise<void> => {
    // L4: block Save when the computed breakdown is STALE — the offer
    // amount or arrears changed after the last Compute, so the CL
    // deduction / VAT / total on screen no longer match the inputs.
    if (
      breakdown &&
      (!computedFor ||
        computedFor.offerAmount !== parseMoney(offerAmount) ||
        computedFor.arrears !== parseMoney(arrearsAmount))
    ) {
      setMessage(null);
      setError(
        'Offer amount or arrears changed since the breakdown was computed — ' +
          'press "Compute breakdown" again before saving.',
      );
      return;
    }

    // Validation guard: earlyMax forces PRD.
    let resolvedDecision = decision;
    if (earlyMax && decision !== 'PRD') {
      resolvedDecision = 'PRD';
      setDecision('PRD');
    }

    // H13: FLAT arrears number + the two model flags -- the backend
    // rejects the old {amount, sameAgreementOnly, notUnderDispute}
    // object at save.
    const patch: Offer = {
      earlyMaxSettlement: earlyMax,
      decision: resolvedDecision,
      remedyType,
      offerAmount: parseMoney(offerAmount),
      arrears: parseMoney(arrearsAmount),
      arrearsSameAgreementOnly: sameAgreementOnly,
      arrearsNotUnderDispute: notUnderDispute,
      ...(breakdown
        ? {
            complexLawDeduction: breakdown.complexLawDeduction,
            vat: breakdown.vat,
            totalDueToClient: breakdown.totalDueToClient,
          }
        : {}),
    };
    await runAction(
      'save-offer',
      () => redressApi.saveOffer(claimId, redressCase.id, agreementId, patch),
      'Offer saved.',
    );
  };

  const handleSaveDates = async (): Promise<void> => {
    await runAction(
      'save-dates',
      () => redressApi.saveDates(claimId, redressCase.id, agreementId, dateDraft),
      'Dates saved.',
    );
  };

  const handleTransitionStatus = async (
    newStatus: RedressStatus,
    actionId: string,
    successMsg: string,
  ): Promise<void> => {
    await runAction(
      actionId,
      () => redressApi.transition(claimId, redressCase.id, agreementId, newStatus),
      successMsg,
    );
  };

  const handleMarkPrdSent = async (): Promise<void> => {
    await runAction(
      'prd-send',
      () =>
        redressApi.markPrdSent(
          claimId,
          redressCase.id,
          agreementId,
          dateDraft.provisionalRedressDate,
        ),
      'PRD marked as sent.',
    );
  };

  // P1-6: RD send is a real workflow action (offer-history snapshot, RD
  // dates, 190-day response link) — not a bare status transition.
  const handleMarkRdSent = async (): Promise<void> => {
    await runAction(
      'rd-send',
      () =>
        redressApi.markRdSent(
          claimId,
          redressCase.id,
          agreementId,
          dateDraft.rdSentDate,
        ),
      'RD marked as sent to client.',
    );
  };

  const handleClientResponse = async (
    response: PrdResponse | RdResponse,
    notes?: string,
  ): Promise<void> => {
    await runAction(
      `response-${response}`,
      () =>
        redressApi.recordClientResponse(
          claimId,
          redressCase.id,
          agreementId,
          response,
          {
            // L4: the response carries its OWN date (empty -> backend
            // stamps today) — not the objection acknowledgement draft.
            date: responseDate || undefined,
            notes,
          },
        ),
      `Client response (${response}) recorded.`,
    );
  };

  const handleAckObjection = async (): Promise<void> => {
    await runAction(
      'ack-objection',
      () =>
        redressApi.acknowledgeObjection(
          claimId,
          redressCase.id,
          agreementId,
          dateDraft.acknowledgementDate,
        ),
      'Objection acknowledged.',
    );
  };

  const handleMarkFd = async (): Promise<void> => {
    await runAction(
      'mark-fd',
      () =>
        redressApi.markFdReceived(
          claimId,
          redressCase.id,
          agreementId,
          dateDraft.finalRedressDate,
        ),
      'FD received.',
    );
  };

  const handleMarkPaid = async (): Promise<void> => {
    const bankReference = window.prompt(
      'Bank reference for this payment (optional, used as ledger idempotency key):',
      '',
    );
    await runAction(
      'mark-paid',
      () =>
        redressApi.markPaid(
          claimId,
          redressCase.id,
          agreementId,
          {
            paymentDate: dateDraft.paymentSentToClientDate,
            bankReference: bankReference || undefined,
          },
        ),
      'Case marked paid.',
    );
  };

  const handleDownloadPrd = async (): Promise<void> => {
    setBusy('prd-letter');
    resetNotices();
    try {
      const blob = await redressApi.downloadPrdLetter(
        claimId, redressCase.id, agreementId, '',
      );
      downloadBlob(
        blob, `redress-prd-${redressCase.proclaimReference}.docx`,
      );
    } catch (err: unknown) {
      setError(await extractDownloadError(err, 'PRD letter download failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadRd = async (): Promise<void> => {
    setBusy('rd-letter');
    resetNotices();
    try {
      const blob = await redressApi.downloadRdLetter(
        claimId, redressCase.id, agreementId, '',
      );
      downloadBlob(
        blob, `redress-rd-${redressCase.proclaimReference}.docx`,
      );
    } catch (err: unknown) {
      setError(await extractDownloadError(err, 'RD letter download failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadFd = async (): Promise<void> => {
    setBusy('fd-letter');
    resetNotices();
    try {
      const blob = await redressApi.downloadFdLetter(
        claimId, redressCase.id, agreementId,
      );
      downloadBlob(
        blob, `redress-fd-${redressCase.proclaimReference}.docx`,
      );
    } catch (err: unknown) {
      setError(await extractDownloadError(err, 'FD letter download failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadFos = async (): Promise<void> => {
    const reason = window.prompt(
      'Rejection reason (shown to the client in the FOS letter):',
      '',
    );
    if (reason === null) return;
    setBusy('fos-letter');
    resetNotices();
    try {
      const blob = await redressApi.downloadFosLetter(
        claimId, redressCase.id, agreementId, reason,
      );
      downloadBlob(
        blob, `redress-fos-${redressCase.proclaimReference}.docx`,
      );
    } catch (err: unknown) {
      setError(await extractDownloadError(err, 'FOS letter download failed'));
    } finally {
      setBusy(null);
    }
  };

  // P2-13 (H10): the reject button only lights where the state machine
  // has an edge into rejected_fos_referral (rd_sent_to_client /
  // fd_received). RD-stage rejection routes through the stage-aware
  // /response endpoint (record_rd_response('object_fos') — records
  // workflow.rdResponse and reports to the state machine); the FD-stage
  // rejection keeps the atomic reject-and-refer wrapper (FOS letter
  // generated in the same round trip). A PRD-stage disagreement is an
  // OBJECTION, never a direct FOS referral — that flow lives on the
  // Objection tab.
  const handleRejectWithPrompt = async (): Promise<void> => {
    const notes = window.prompt(
      'Reason for rejection (saved with the case and shown in letters):',
      '',
    );
    if (notes === null) return;
    const trimmed = notes.trim();
    if (!trimmed) {
      setError('A rejection reason is required to refer to FOS.');
      return;
    }
    if (agreement.workflow.status === 'rd_sent_to_client') {
      await runAction(
        'response-rejected',
        () => redressApi.recordClientResponse(
          claimId,
          redressCase.id,
          agreementId,
          'object_fos',
          { date: responseDate || undefined, notes: trimmed },
        ),
        'RD rejection recorded — referred to FOS.',
      );
      return;
    }
    await runAction(
      'response-rejected',
      () => redressApi.rejectAndReferToFos(
        claimId,
        redressCase.id,
        agreementId,
        trimmed,
        { date: responseDate || undefined },
      ),
      'Client rejection recorded and FOS referral letter generated.',
    );
  };

  // --- Status-based affordance toggles --------------------------------------

  const status: RedressStatus = agreement.workflow.status;

  type ActionGate = {
    allowed: boolean;
    requires: string;
  };

  // P2-13 (H10): every gate derives from ``canTransition`` — the frontend
  // mirror of the backend ALLOWED_TRANSITIONS table (api/redress.ts, keep
  // in sync) — so a button is enabled exactly when the server would
  // accept the action from the current status. The previous ad-hoc gates
  // had three 100%-409 offenders: "Escalate to RD" required awaiting_rd
  // (a same-state transition), "Record client reject" lit at
  // prd_sent_to_client (no rejected_fos_referral edge — PRD-stage
  // disagreement is an objection), and "Mark paid" lit at
  // rd_sent_to_client (no -> paid edge; nothing pays on silence).
  const gates = useMemo((): Record<string, ActionGate> => {
    return {
      markJoined: {
        allowed: canTransition(status, 'joined'),
        requires: 'Requires status: Invited',
      },
      markAwaitingPrd: {
        allowed: canTransition(status, 'awaiting_prd'),
        requires: 'Requires status: Joined or Held (high value)',
      },
      markPrdReceived: {
        allowed: canTransition(status, 'prd_received'),
        requires: 'Requires status: Awaiting PRD or Held (high value)',
      },
      prdSend: {
        // mark_prd_sent guards the prd_sent_to_client transition, and is
        // additionally idempotent AT prd_sent_to_client (the upheld-
        // objection loop re-issues a PRD, refreshing dates + link).
        allowed:
          canTransition(status, 'prd_sent_to_client') ||
          status === 'prd_sent_to_client',
        requires:
          'Requires status: PRD received or Objection submitted ' +
          '(re-send allowed at PRD sent to client)',
      },
      clientAccept: {
        // The /response endpoint dispatches by stage: PRD accept from
        // prd_sent_to_client, RD accept from rd_sent_to_client — exactly
        // the two statuses with a client_accepted edge.
        allowed: canTransition(status, 'client_accepted'),
        requires: 'Requires status: PRD sent to client or RD sent to client',
      },
      clientReject: {
        allowed: canTransition(status, 'rejected_fos_referral'),
        requires:
          'Requires status: RD sent to client or FD received ' +
          '(a PRD-stage disagreement is an objection — use the Objection tab)',
      },
      ackObjection: {
        // Non-transitioning action, but it acknowledges a REGISTERED
        // objection — meaningless before record_objection has moved the
        // agreement to client_objecting.
        allowed: status === 'client_objecting',
        requires: 'Requires status: Client objecting (register the objection first)',
      },
      markFd: {
        allowed: canTransition(status, 'fd_received'),
        requires: 'Requires status: Objection submitted or Client accepted',
      },
      markPaid: {
        allowed: canTransition(status, 'paid'),
        requires: 'Requires status: Client accepted or FD received',
      },
      escalateToRd: {
        allowed: canTransition(status, 'awaiting_rd'),
        requires:
          'Requires status: PRD sent to client (PRD window lapsed, no response)',
      },
      markRdSent: {
        // P1-6: rd/send is guard-first server-side — only statuses with an
        // rd_sent_to_client edge (awaiting_rd, objection_submitted, and —
        // Batch-G review fix 2 — client_objecting, for the RD the lender
        // issues after the formal-objection window expires) succeed.
        allowed: canTransition(status, 'rd_sent_to_client'),
        requires:
          'Requires status: Awaiting RD, Objection submitted or ' +
          'Client objecting',
      },
    };
  }, [status]);

  // --- Render ----------------------------------------------------------------

  const nextAction = deriveNextAction(agreement);

  // VAT inline suffix for the CL deduction row.
  const incVat =
    (breakdown?.complexLawDeduction ?? 0) + (breakdown?.vat ?? 0);

  return (
    <div>
      {/* Next action banner */}
      <div style={bannerInfoStyle} role="status" aria-live="polite">
        <strong>Next action:</strong> {nextAction}
      </div>

      {/* Client response link (visible after Mark PRD/RD sent). P1-5: the
          link is minted PER-AGREEMENT (agreement.documents.responseLink);
          the case-level fallback only serves pre-migration cases. */}
      {(() => {
        const agreementLink = agreement.documents?.responseLink;
        const caseLink = redressCase.documents?.responseLink;
        const link =
          typeof agreementLink === 'string' && agreementLink
            ? agreementLink
            : typeof caseLink === 'string'
              ? caseLink
              : '';
        if (!link) return null;
        return (
          <div
            style={{
              border: `1px solid ${colors.primary}`,
              background: '#eff6ff',
              borderRadius: 6,
              padding: 12,
              margin: '12px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
            role="region"
            aria-label="Client response link"
          >
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: colors.neutral600,
                  marginBottom: 4,
                }}
              >
                Client response link
              </div>
              <code
                style={{
                  display: 'block',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  color: colors.neutral800,
                  background: '#fff',
                  border: `1px solid ${colors.neutral300}`,
                  borderRadius: 4,
                  padding: '6px 8px',
                }}
              >
                {link}
              </code>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(link).then(() =>
                    setMessage('Response link copied to clipboard.'),
                  );
                }}
                style={buttonPrimary}
              >
                Copy link
              </button>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...buttonSecondary, textDecoration: 'none' }}
              >
                Open
              </a>
            </div>
          </div>
        );
      })()}

      {/* Offer breakdown */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Offer breakdown</legend>

        {/* Early max settlement / decision / remedy type */}
        <div style={offerInputsGrid}>
          <label style={labelStackStyle}>
            Early max settlement?
            <select
              value={earlyMax ? 'yes' : 'no'}
              onChange={(e) => {
                const v = e.target.value === 'yes';
                setEarlyMax(v);
                if (v) setDecision('PRD'); // Yes forces PRD per §8.1
              }}
              style={inputStyle}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          <label style={labelStackStyle}>
            Decision
            <select
              value={decision}
              onChange={(e) => setDecision(e.target.value as OfferDecision)}
              disabled={earlyMax}
              style={applyDisabled(inputStyle, earlyMax)}
            >
              <option value="PRD">PRD</option>
              <option value="RD">RD</option>
            </select>
          </label>
          <label style={labelStackStyle}>
            Remedy type
            <select
              value={remedyType}
              onChange={(e) => setRemedyType(e.target.value as RemedyType)}
              style={inputStyle}
            >
              <option value="hybrid">Hybrid</option>
              <option value="johnson">Johnson</option>
            </select>
          </label>
        </div>

        {/* Offer amount + arrears inputs */}
        <div style={{ ...offerInputsGrid, marginTop: 12 }}>
          <label style={labelStackStyle}>
            Offer made (£)
            <input
              type="number"
              step="0.01"
              min="0"
              value={offerAmount}
              onChange={(e) => setOfferAmount(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Arrears (£)
            <input
              type="number"
              step="0.01"
              min="0"
              value={arrearsAmount}
              onChange={(e) => setArrearsAmount(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        {/* Arrears eligibility flags */}
        <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={arrearsCheckboxLabel}>
            <input
              type="checkbox"
              checked={sameAgreementOnly}
              onChange={(e) => setSameAgreementOnly(e.target.checked)}
            />
            Same agreement only
          </label>
          <label style={arrearsCheckboxLabel}>
            <input
              type="checkbox"
              checked={notUnderDispute}
              onChange={(e) => setNotUnderDispute(e.target.checked)}
            />
            Not under dispute
          </label>
        </div>
        <div style={hintStyle}>
          Set-off only valid when both are ticked.
        </div>

        <table style={{ ...tableStyle, maxWidth: 480, marginTop: 12 }}>
          <tbody>
            <BreakdownRow label="Offer made" amount={breakdown?.offerAmount} />
            <BreakdownRow label="Arrears" amount={breakdown?.arrears} />
            <BreakdownRow
              label="Complex Law deduction"
              amount={breakdown?.complexLawDeduction}
              suffix={
                breakdown
                  ? `(£${formatMoney(incVat)} inc VAT)`
                  : undefined
              }
            />
            <BreakdownRow label="VAT" amount={breakdown?.vat} />
            <BreakdownRow
              label="Total due to client"
              amount={breakdown?.totalDueToClient}
              emphasis
            />
          </tbody>
        </table>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => void handleCompute()}
            disabled={busy !== null}
            style={applyDisabled(buttonSecondary, busy !== null)}
            aria-busy={busy === 'compute'}
          >
            {busy === 'compute' ? 'Computing…' : 'Compute breakdown'}
          </button>
          <button
            type="button"
            onClick={() => void handleSaveOffer()}
            disabled={busy !== null}
            style={applyDisabled(buttonPrimary, busy !== null)}
            aria-busy={busy === 'save-offer'}
          >
            {busy === 'save-offer' ? 'Saving…' : 'Save offer'}
          </button>
        </div>
      </fieldset>

      {/* PRD / RD client response read-only panel */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Client response</legend>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={subLabelStyle}>PRD response</div>
            <span style={pillForResponse(agreement.workflow.prdResponse)}>
              {humaniseStatus(responseValue(agreement.workflow.prdResponse))}
            </span>
            <div style={hintStyle}>
              accept / object · auto no_response after 1 month
            </div>
            {/* Batch G (PS26/3): a PRD acceptance is full-and-final. */}
            {agreement.workflow.fosRightsWaived ? (
              <div
                style={{ ...hintStyle, color: colors.neutral700 }}
                role="note"
              >
                <strong>PRD accepted — FOS rights waived</strong> (full and
                final; an RD-stage acceptance would keep them).
              </div>
            ) : null}
          </div>
          <div>
            <div style={subLabelStyle}>RD response</div>
            <span style={pillForResponse(agreement.workflow.rdResponse)}>
              {humaniseStatus(responseValue(agreement.workflow.rdResponse))}
            </span>
            <div style={hintStyle}>
              accept / object→FOS / opt-out · auto no_response after 6 months
            </div>
          </div>
          <div>
            {/* L4: the accept/reject actions carry their own date. */}
            <label style={labelStackStyle}>
              Response date (for record accept / reject)
              <input
                type="date"
                value={responseDate}
                onChange={(e) => setResponseDate(e.target.value)}
                style={inputStyle}
              />
            </label>
            <div style={hintStyle}>Leave empty to record today.</div>
          </div>
        </div>
      </fieldset>

      {/* Dates panel */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Key dates</legend>
        <div style={datesGridStyle}>
          {DATE_FIELDS.map(({ key, label }) => (
            <label key={key} style={labelStackStyle}>
              {label}
              <input
                type="date"
                value={dateDraft[key] ?? ''}
                onChange={(e) =>
                  setDateDraft((prev) => ({
                    ...prev,
                    [key]: e.target.value || undefined,
                  }))
                }
                style={inputStyle}
              />
            </label>
          ))}
          {/* Batch G (CONRED 5.4.37R(6)): rd_expected chase context —
              stamped by the PRD-lapse path, read-only here (never part
              of the editable draft/save). */}
          {agreement.dates?.rdExpectedByDate ? (
            <label style={labelStackStyle}>
              RD expected by (lender deadline)
              <input
                type="date"
                readOnly
                value={agreement.dates.rdExpectedByDate}
                style={{
                  ...inputStyle,
                  background: colors.neutral100,
                  color: colors.neutral600,
                }}
              />
              <span style={hintStyle}>
                PRD lapsed — lender must issue an RD confirming the PRD
                (chased via rd_expected reminders).
              </span>
            </label>
          ) : null}
        </div>
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => void handleSaveDates()}
            disabled={busy !== null}
            style={applyDisabled(buttonPrimary, busy !== null)}
            aria-busy={busy === 'save-dates'}
          >
            {busy === 'save-dates' ? 'Saving…' : 'Save dates'}
          </button>
        </div>
      </fieldset>

      {/* Action strip */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Case actions</legend>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <GatedButton
            label="Mark client joined"
            gate={gates.markJoined}
            busy={busy !== null}
            busyLabel={busy === 'transition-joined' ? 'Saving…' : undefined}
            onClick={() =>
              void handleTransitionStatus(
                'joined',
                'transition-joined',
                'Client marked as joined.',
              )
            }
            variant="secondary"
          />
          <GatedButton
            label="Mark awaiting PRD"
            gate={gates.markAwaitingPrd}
            busy={busy !== null}
            busyLabel={busy === 'transition-awaiting' ? 'Saving…' : undefined}
            onClick={() =>
              void handleTransitionStatus(
                'awaiting_prd',
                'transition-awaiting',
                'Status set to awaiting PRD.',
              )
            }
            variant="secondary"
          />
          <GatedButton
            label="Mark PRD received"
            gate={gates.markPrdReceived}
            busy={busy !== null}
            busyLabel={busy === 'transition-received' ? 'Saving…' : undefined}
            onClick={() =>
              void handleTransitionStatus(
                'prd_received',
                'transition-received',
                'PRD recorded as received from lender.',
              )
            }
            variant="secondary"
          />
          <GatedButton
            label="Mark PRD sent"
            gate={gates.prdSend}
            busy={busy !== null}
            busyLabel={busy === 'prd-send' ? 'Saving…' : undefined}
            onClick={() => void handleMarkPrdSent()}
            variant="secondary"
          />
          <GatedButton
            label="Record client accept"
            gate={gates.clientAccept}
            busy={busy !== null}
            busyLabel={busy === 'response-accepted' ? 'Saving…' : undefined}
            onClick={() => void handleClientResponse('accept')}
            variant="secondary"
          />
          <GatedButton
            label="Record client reject"
            gate={gates.clientReject}
            busy={busy !== null}
            busyLabel={busy === 'response-rejected' ? 'Saving…' : undefined}
            onClick={() => void handleRejectWithPrompt()}
            variant="danger"
          />
          <GatedButton
            label="Acknowledge objection"
            gate={gates.ackObjection}
            busy={busy !== null}
            busyLabel={busy === 'ack-objection' ? 'Saving…' : undefined}
            onClick={() => void handleAckObjection()}
            variant="secondary"
          />
          <GatedButton
            label="Mark FD received"
            gate={gates.markFd}
            busy={busy !== null}
            busyLabel={busy === 'mark-fd' ? 'Saving…' : undefined}
            onClick={() => void handleMarkFd()}
            variant="secondary"
          />
          <GatedButton
            label="Escalate to RD"
            gate={gates.escalateToRd}
            busy={busy !== null}
            busyLabel={busy === 'transition-awaiting-rd' ? 'Saving…' : undefined}
            onClick={() =>
              void handleTransitionStatus(
                'awaiting_rd',
                'transition-awaiting-rd',
                'Status set to awaiting RD.',
              )
            }
            variant="secondary"
          />
          <GatedButton
            label="Mark RD sent"
            gate={gates.markRdSent}
            busy={busy !== null}
            busyLabel={busy === 'rd-send' ? 'Saving…' : undefined}
            onClick={() => void handleMarkRdSent()}
            variant="secondary"
          />
          <GatedButton
            label="Mark paid"
            gate={gates.markPaid}
            busy={busy !== null}
            busyLabel={busy === 'mark-paid' ? 'Saving…' : undefined}
            onClick={() => void handleMarkPaid()}
            variant="primary"
          />
        </div>
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 12,
            color: colors.neutral500,
          }}
        >
          Actions are enabled only for the current status (
          <code>{status}</code>). Hover a disabled button to see what is
          required.
        </p>
      </fieldset>

      {/* Letter downloads */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Letters</legend>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={() => void handleDownloadPrd()}
            disabled={busy !== null}
            style={applyDisabled(buttonSecondary, busy !== null)}
            aria-busy={busy === 'prd-letter'}
          >
            {busy === 'prd-letter' ? 'Preparing…' : 'Download PRD letter'}
          </button>
          <button
            type="button"
            onClick={() => void handleDownloadRd()}
            disabled={busy !== null}
            style={applyDisabled(buttonSecondary, busy !== null)}
            aria-busy={busy === 'rd-letter'}
          >
            {busy === 'rd-letter' ? 'Preparing…' : 'Download RD letter'}
          </button>
          <button
            type="button"
            onClick={() => void handleDownloadFd()}
            disabled={busy !== null}
            style={applyDisabled(buttonSecondary, busy !== null)}
            aria-busy={busy === 'fd-letter'}
          >
            {busy === 'fd-letter' ? 'Preparing…' : 'Download FD letter'}
          </button>
          <button
            type="button"
            onClick={() => void handleDownloadFos()}
            disabled={busy !== null}
            style={applyDisabled(buttonSecondary, busy !== null)}
            aria-busy={busy === 'fos-letter'}
          >
            {busy === 'fos-letter' ? 'Preparing…' : 'Download FOS letter'}
          </button>
        </div>
      </fieldset>

      {message !== null && (
        <div style={{ ...bannerSuccessStyle, marginTop: 12 }} role="status">
          {message}
        </div>
      )}
      {error !== null && (
        <div style={{ ...bannerDangerStyle, marginTop: 12 }} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

// --- Sub-components ----------------------------------------------------------

interface BreakdownRowProps {
  label: string;
  amount: number | undefined;
  emphasis?: boolean;
  suffix?: string;
}

function BreakdownRow({
  label,
  amount,
  emphasis,
  suffix,
}: BreakdownRowProps): React.ReactElement {
  return (
    <tr style={emphasis ? { background: colors.neutral50 } : undefined}>
      <td
        style={{
          ...tdStyle,
          fontWeight: emphasis ? 700 : 400,
          color: emphasis ? colors.neutral900 : colors.neutral700,
        }}
      >
        {label}
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: emphasis ? 700 : 500,
          color: emphasis ? colors.neutral900 : colors.neutral800,
        }}
      >
        £&nbsp;{formatMoney(amount)}
        {suffix ? (
          <span
            style={{
              marginLeft: 6,
              fontSize: 12,
              color: colors.neutral500,
              fontWeight: 400,
            }}
          >
            {suffix}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

interface GatedButtonProps {
  label: string;
  gate: { allowed: boolean; requires: string };
  busy: boolean;
  busyLabel?: string;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'danger';
}

function GatedButton({
  label,
  gate,
  busy,
  busyLabel,
  onClick,
  variant,
}: GatedButtonProps): React.ReactElement {
  const disabled = !gate.allowed || busy;
  const base =
    variant === 'primary'
      ? buttonPrimary
      : variant === 'danger'
      ? buttonDanger
      : buttonSecondary;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-busy={busy && !!busyLabel}
      title={!gate.allowed ? gate.requires : undefined}
      style={applyDisabled(base, disabled)}
    >
      {busy && busyLabel ? busyLabel : label}
    </button>
  );
}

// --- Inline styles -----------------------------------------------------------

const offerInputsGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  maxWidth: 600,
};

const datesGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const arrearsCheckboxLabel: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: colors.neutral700,
  cursor: 'pointer',
};

const subLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: colors.neutral600,
  marginBottom: 4,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: colors.neutral500,
  marginTop: 4,
};
